import { useState } from 'react';
import PropTypes from 'prop-types';
import { useConfig } from '../configContext.js';
import AccountPicker from './AccountPicker.jsx';
import InviteeGroups from './InviteeGroups.jsx';
import Modal from './Modal.jsx';

function formatToZimbra(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

function toLocalDatetimeString(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const LABEL = 'mb-1 block text-sm font-medium text-gray-700';
const FIELD =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500';

export default function CreateAppointment({
    onClose,
    selectionInfo,
    setMessage,
    setColor,
    setSnackbarOpen,
    onSuccess,
}) {
    const { apiUrl } = useConfig();

    // Home mounts this dialog afresh on every opening, so `selectionInfo` is
    // fixed for the component's lifetime: the form reads it once, as initial
    // state. An effect that copied props into state on each change was both
    // redundant and a source of cascading renders.
    const [subject, setSubject] = useState('');
    const [location, setLocation] = useState('');
    const [start, setStart] = useState(() =>
        selectionInfo?.start ? toLocalDatetimeString(selectionInfo.start) : '');
    const [end, setEnd] = useState(() =>
        selectionInfo?.end ? toLocalDatetimeString(selectionInfo.end) : '');
    const [attendees, setAttendees] = useState(() => {
        if (selectionInfo?.prefilledAttendees?.length > 0) return selectionInfo.prefilledAttendees;
        if (selectionInfo?.resourceEmail) {
            return [{ name: selectionInfo.resourceEmail, uuid: selectionInfo.resourceUuid }];
        }
        return [];
    });
    // Invited groups stay grouped: a flat list of ninety-seven chips buried
    // the rest of the form. `excludedMembers` holds the ones unticked inside.
    const [invitedGroups, setInvitedGroups] = useState(() => selectionInfo?.invitedGroups || []);
    const [excludedMembers, setExcludedMembers] = useState(() => new Set());
    const [description, setDescription] = useState('');
    const [fb, setFb] = useState('B');
    const [submitting, setSubmitting] = useState(false);

    const notify = (message, color) => {
        setMessage(message);
        setColor(color);
        setSnackbarOpen(true);
    };

    const toggleGroupMember = (uuid) => {
        setExcludedMembers((current) => {
            const next = new Set(current);
            if (next.has(uuid)) next.delete(uuid);
            else next.add(uuid);
            return next;
        });
    };

    const removeGroup = (groupId) => {
        setInvitedGroups((current) => current.filter((group) => group.id !== groupId));
    };

    /** Everyone actually invited: the groups minus their exclusions, plus the
     *  people added by hand. Addresses are deduplicated — someone can belong
     *  to two groups, or be added on top of one. */
    const allAttendees = () => {
        const byName = new Map();
        for (const group of invitedGroups) {
            for (const member of group.members) {
                if (excludedMembers.has(member.uuid)) continue;
                byName.set(member.name, member);
            }
        }
        for (const person of attendees) byName.set(person.name, person);
        return [...byName.values()];
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!subject.trim()) return notify("L'objet est requis.", 'danger');
        const invitees = allAttendees();
        if (invitees.length === 0) return notify('Au moins un invité est requis.', 'danger');

        setSubmitting(true);
        try {
            const response = await fetch(`${apiUrl}/appointments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subject: subject.trim(),
                    location,
                    start: formatToZimbra(new Date(start)),
                    end: formatToZimbra(new Date(end)),
                    attendees: invitees.map((a) => ({ email: a.name })),
                    description,
                    fb,
                }),
            });
            const data = await response.json();
            if (!data.success) return notify(data.message || "Erreur lors de l'envoi.", 'danger');

            notify('Invitation envoyée avec succès.', 'success');
            onClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            notify('Erreur : ' + error.message, 'danger');
        } finally {
            setSubmitting(false);
        }
        return undefined;
    };

    return (
        <Modal title="Nouveau rendez-vous" onClose={onClose} width="max-w-2xl">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                    <label htmlFor="appt-subject" className={LABEL}>Objet *</label>
                    <input
                        id="appt-subject"
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        placeholder="Objet du rendez-vous"
                        required
                        className={FIELD}
                    />
                </div>

                <div>
                    <label htmlFor="appt-location" className={LABEL}>Lieu</label>
                    <input
                        id="appt-location"
                        value={location}
                        onChange={(event) => setLocation(event.target.value)}
                        placeholder="Lieu"
                        className={FIELD}
                    />
                </div>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <label htmlFor="appt-start" className={LABEL}>Début *</label>
                        <input
                            id="appt-start"
                            type="datetime-local"
                            step={300}
                            value={start}
                            onChange={(event) => setStart(event.target.value)}
                            required
                            className={FIELD}
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="appt-end" className={LABEL}>Fin *</label>
                        <input
                            id="appt-end"
                            type="datetime-local"
                            step={300}
                            value={end}
                            onChange={(event) => setEnd(event.target.value)}
                            required
                            className={FIELD}
                        />
                    </div>
                </div>

                <div>
                    <span className={LABEL}>
                        Invités *
                        <span className="ml-2 font-normal text-gray-500">
                            {allAttendees().length} au total
                        </span>
                    </span>
                    <InviteeGroups
                        groups={invitedGroups}
                        excluded={excludedMembers}
                        onToggleMember={toggleGroupMember}
                        onRemoveGroup={removeGroup}
                    />
                    <AccountPicker
                        value={attendees}
                        onChange={setAttendees}
                        placeholder="Ajouter un invité"
                    />
                </div>

                <div>
                    <label htmlFor="appt-description" className={LABEL}>Description</label>
                    <textarea
                        id="appt-description"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        rows={3}
                        placeholder="Notes..."
                        className={FIELD}
                    />
                </div>

                <div>
                    <label htmlFor="appt-fb" className={LABEL}>Disponibilité</label>
                    <select
                        id="appt-fb"
                        value={fb}
                        onChange={(event) => setFb(event.target.value)}
                        className={FIELD}
                    >
                        <option value="B">Occupé</option>
                        <option value="F">Libre</option>
                        <option value="T">Tentative</option>
                        <option value="O">Absent</option>
                    </select>
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className="self-start rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                    {submitting ? 'Envoi…' : "Envoyer l'invitation"}
                </button>
            </form>
        </Modal>
    );
}

CreateAppointment.propTypes = {
    onClose: PropTypes.func.isRequired,
    selectionInfo: PropTypes.object,
    setMessage: PropTypes.func.isRequired,
    setColor: PropTypes.func.isRequired,
    setSnackbarOpen: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};
